import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import Modal from 'ui/Modal'
import { updateTeamTopIssue } from 'util/teamUtils'
import IssueSearch, { Suggestion } from 'components/Issues/IssueSearch'
import api from 'config/axiosConfig'
import { Issue, Team } from 'util/constants'
import { toast } from 'sonner'
import ActorPrivacy from 'components/Common/ActorPrivacy'
import { parseWikipediaData } from 'util/wikiUtils'

interface Props {
  team: Team;
  onClose: () => void;
}

const TeamAboutIssueModal: React.FC<Props> = ({ team, onClose }) => {
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prepopulatedIssue, setPrepopulatedIssue] = useState<Issue | null>(null);
  const [issueId, setIssueId] = useState<number | null>(team.about?.bio_issue?.value || null);    
  let isButtonDisabled = isSubmitting || (!prepopulatedIssue && !selectedIssue);

  useEffect(() => {
    const fetchExistingIssue = async () => {
      const existingIssueId = team.about?.bio_issue?.value || null;
      if (existingIssueId) {
        try {
          const response = await api.get<Issue>(`/api/issues/id/${existingIssueId}`);
          setPrepopulatedIssue(response.data);
          setIssueId(existingIssueId);
        } catch (error) {
          console.error('Error fetching existing issue:', error);
          setError('Failed to load existing issue.');
        }
      }
    };

    fetchExistingIssue();
  }, [team.about?.bio_issue?.value]);
  
  const uploadIssueImage = async (imageUrl: string, issueId: number) => {
      if (!imageUrl) return null;
      
      const imageResponse = await fetch(imageUrl);

      let contentType = imageResponse.headers.get('content-type');

      if (!contentType) {
        contentType = 'image/jpeg';
      }
      if (contentType.includes('image/svg+xml')) {
        contentType = 'image/svg';
      }
      if (contentType.includes('image/jpeg')) {
        contentType = 'image/jpg';
      }

      const blob = await imageResponse.blob();
          
      if (blob) {
          try {
              const formData = new FormData();
              formData.append('image', blob, `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`);
              const response = await api.post(`/api/issues/image/${issueId}`, formData);
              return response.data.url;
          } catch (err) {
              console.error('Error uploading reference image:', err);
              return null;
          }
      }
  };

  const handleIssueSelect = async (suggestion: Suggestion) => {
    setError(null);
    isButtonDisabled = true;

    if (suggestion.type === 'wiki') {
      try {
        // First, parse wikipedia data to extract image details
        const wikiImg = await parseWikipediaData(suggestion.cname, true);
        // Create new issue from the wiki suggestion with extra properties
        const response = await api.post(`/api/issues`, {
          name: suggestion.name,
          cname: suggestion.cname,
          subject_cname: suggestion.cname,
          wikipedia_link: suggestion.link,
          subject_properties: {
            ...suggestion.subject_properties,
            wiki_parse_first_img: wikiImg.wiki_parse_first_img
          }
        });
        // If a wiki image was found, upload it
        if (response.data.properties.subject_properties.wiki_parse_first_img) {
          await uploadIssueImage(
            response.data.properties.subject_properties.wiki_parse_first_img,
            response.data.id
          );
        }
        setIssueId(response.data.id);
        toast.success('New issue created!');
      } catch (error) {
        console.error('Error creating new issue:', error);
        toast.error('Failed to create new issue.');
        setError('Failed to create new issue.');
        return;
      }
    } else {
      setIssueId(suggestion.id);
    }

    setSelectedIssue(suggestion);
    isButtonDisabled = false;
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await updateTeamTopIssue(team.id, issueId);
      if (team.about?.bio_issue?.value) {
        team.about.bio_issue.value = issueId;
      }
      toast.success('Top issue updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating top issue:', error);
      setError('Failed to update top issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    onClose();
  };

  const handleEntityChipClose = () => {
    setSelectedIssue(null);
    setIssueId(null);
  };

  isButtonDisabled = isSubmitting || (!prepopulatedIssue && !selectedIssue);

  return (
    <Modal isOpen={true} onClose={handleClose} title="#1 Issue">
      <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            What's the most pressing issue to your partea? This will be displayed on your partea's profile.
          </p>

          <div className="form-control space-y-4">
            <div className="relative">
              <IssueSearch
                onSelect={handleIssueSelect}
                placeholder="Search for an issue"
                inputType="add"
                fieldRequired={false}
                prepopulateData={prepopulatedIssue}
                onEntityChipClose={handleEntityChipClose}
              />
            </div>

            <div className="relative">
              <ActorPrivacy 
                privacy={'public'}
                settingKey="bio_issue"
                onPrivacyChange={() => {}}
                actorType="team"
                editMode={true}
                showDropdown={false}
                miniIcon={false}
                disabled={true}
                className="opacity-50"
                viewerRelation={team.viewerRelation}
              />
            </div>

            {error && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                <Info className="mr-1 size-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary w-full sm:w-fit py-2 px-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            className={`w-full sm:w-fit py-2 px-4 ${
              isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TeamAboutIssueModal;
